import { Dispatch, SetStateAction } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { DynamicTableCellConfig } from '~/legacy-components/LegacyDynamicFormTable/types';
import { isRoleHRAdmin } from '~/services/Permissions';

import { IFormInput } from '../useEquipmentTab';

import { EquipListType, Equipment } from '.';

export const getCellConfig = (
  equipEnumType: Equipment[],
  equipList: EquipListType[],
  setEditedRows: Dispatch<SetStateAction<number[]>>,
  useFormMethods: UseFormReturn<IFormInput>,
  myRoles: string[],
) => {
  const {
    setValue,
    formState: { errors },
    watch,
  } = useFormMethods;

  const addIndexEditedRows = (index: number) =>
    setEditedRows((prevState) => {
      if (prevState.includes(index)) {
        return prevState;
      }
      return [...prevState, index];
    });

  const additionalOnChange = (
    index: number,
    value: string,
    type: 'fComEquipmentInvNumber' | 'fComEquipmentItem',
  ) => {
    watch(`equipment.${index}.fEquipmentCompEqFK`);
    const findValue = equipList.find((el) => el[type] === value);
    setValue(
      `equipment.${index}.fEquipmentType`,
      findValue?.fComEquipmentType || '',
    );
    setValue(
      `equipment.${index}.fEquipmentCompEqFK`,
      findValue?.fComEquipmentId,
    );
    setValue(
      `equipment.${index}.fEquipmentExpirationDate`,
      findValue?.fComEquipmentExpirationDate || null,
    );
    if (type === 'fComEquipmentInvNumber') {
      setValue(
        `equipment.${index}.fEquipmentItem`,
        findValue?.fComEquipmentItem,
      );
    } else {
      setValue(
        `equipment.${index}.fEquipmentInvNumber`,
        findValue?.fComEquipmentInvNumber || '',
      );
    }
  };

  const onChangeFunction = {
    onChange: (
      index: number,
      onChange?: Function,
      value?: string | File | null,
    ) => {
      if (onChange) {
        onChange(value);
      }
    },
  };

  const columnConfigs = {
    fEquipmentType: {
      type: 'select',
      clearable: false,
      width: '150px',
      disabled: true,
      options: equipEnumType.map((eEnum) => ({
        value: eEnum.code,
        label: eEnum.name,
      })),
      ...onChangeFunction,
      error: (index: number) =>
        errors.equipment &&
        errors.equipment[index] &&
        errors.equipment[index]!.fEquipmentType
          ? true
          : false,
      controllerConfig: {
        rules: { required: true },
      },
    },
    fEquipmentItem: {
      type: 'input',
      width: '170px',
      disabled: true,
      ...onChangeFunction,
    },
    fEquipmentInvNumber: {
      type: 'input',
      width: '125px',
      disabled: false,
      ...onChangeFunction,
      error: (index: number) =>
        errors.equipment &&
        errors.equipment[index] &&
        errors.equipment[index]!.fEquipmentInvNumber
          ? true
          : false,
      controllerConfig: {
        rules: { required: true },
      },
    },
    fEquipmentDeliveryDate: {
      type: 'date',
      width: '105px',
      ...onChangeFunction,
      error: (index: number) =>
        errors.equipment &&
        errors.equipment[index] &&
        errors.equipment[index]!.fEquipmentDeliveryDate
          ? true
          : false,
      controllerConfig: {
        rules: { required: true },
      },
    },
    fEquipmentReturnDate: {
      type: 'date',
      width: '105px',
      disableFuture: true,
      ...onChangeFunction,
    },
    fEquipmentInfo: {
      type: 'input',
      width: '148px',
      ...onChangeFunction,
    },
    fEquipmentAttachmentName: {
      type: 'attachment',
      width: '170px',
      onChange: (
        index: number,
        onChange?: Function,
        value?: string | File | null,
      ) => {
        if (onChange) {
          onChange(value ? (value as File).name : '');
          setValue(
            `equipment.${index}.fEquipmentAttachment`,
            value as File | undefined,
          );
          setValue(`equipment.${index}.fEquipmentAttachmentId`, -1);
        }
      },
    },
    fEquipmentExpirationDate: {
      type: 'date',
      width: '100px',
      ...onChangeFunction,
    },
  };

  const rowConfigs = {
    fEquipmentItem: {
      type: 'select',
      clearable: false,
      disabled: false,
      options: equipList
        .map((equip) => ({
          value: equip?.fComEquipmentItem || '',
          label: equip?.fComEquipmentItem || '',
        }))
        .filter((values) => !(values.value === '' && values.label === '')),
      onChange: (
        index: number,
        onChange?: Function,
        value?: string | File | null,
      ) => {
        if (onChange && value) {
          additionalOnChange(index, value as string, 'fComEquipmentItem');
          onChange(value);
        }
      },
    },
    fEquipmentInvNumber: {
      type: 'select',
      clearable: false,
      disabled: false,
      onChange: (
        index: number,
        onChange?: Function,
        value?: string | File | null,
      ) => {
        if (onChange && value) {
          additionalOnChange(index, value as string, 'fComEquipmentInvNumber');
          onChange(value);
        }
      },
      options: equipList.map((equip) => ({
        value: equip?.fComEquipmentInvNumber || '',
        label: equip?.fComEquipmentInvNumber || '',
      })),
    },
  };

  const getCellConfig = (
    cellName: string,
    index: number,
    findEquipList: EquipListType | undefined,
    bnNewCompanyItem: boolean | undefined,
    fEquipmentId?: number,
    value?: string | number,
  ) => {
    const fieldName = `equipment.${index}.${cellName}`;
    const columnConfigForCell: DynamicTableCellConfig =
      columnConfigs[cellName as keyof typeof columnConfigs];
    const rowConfigForCell = rowConfigs[cellName as keyof typeof rowConfigs];

    const customConfigs = {
      ...(bnNewCompanyItem === false ? { ...rowConfigForCell } : {}),
      ...(bnNewCompanyItem === true &&
      ['fEquipmentType', 'fEquipmentItem', 'fEquipmentInvNumber'].includes(
        cellName,
      )
        ? { disabled: false }
        : {}),
      ...(bnNewCompanyItem === undefined
        ? {
            onChange: (
              index: number,
              onChange: Function,
              value: string & File,
            ) => {
              columnConfigForCell.onChange(index, onChange, value);
              if (fEquipmentId) {
                addIndexEditedRows(fEquipmentId);
              }
            },
          }
        : {}),
      ...(cellName === 'fEquipmentExpirationDate'
        ? {
            disabled:
              !isRoleHRAdmin(myRoles) ||
              (findEquipList && findEquipList.fComEquipmentExpirationDate)
                ? true
                : false,
          }
        : {}),
      ...(bnNewCompanyItem !== undefined
        ? {
            controllerConfig: {
              ...(columnConfigForCell.controllerConfig
                ? columnConfigForCell.controllerConfig
                : {}),
              defaultValue: value,
            },
          }
        : {}),
    };
    return {
      name: fieldName,
      ...columnConfigForCell,
      ...customConfigs,
    } as DynamicTableCellConfig;
  };

  return getCellConfig;
};
